import isTouch from "../../utils/isTouch";

const BUS_STORAGE_KEY = "scan.followedBus";
const DEVICE_STORAGE_KEY = "scan.deviceRef";

export type FollowedBus = { tripId: number; day: string; setBy?: number };

export const readFollowedBus = (): FollowedBus | null => {
  try {
    const stored = JSON.parse(localStorage.getItem(BUS_STORAGE_KEY) ?? "null") as FollowedBus | null;
    return stored && typeof stored.tripId === "number" && typeof stored.day === "string" ? stored : null;
  } catch {
    return null;
  }
};

export const storeFollowedBus = (bus: FollowedBus | null) => {
  try {
    if (bus) localStorage.setItem(BUS_STORAGE_KEY, JSON.stringify(bus));
    else localStorage.removeItem(BUS_STORAGE_KEY);
  } catch {
    // Private mode: the bus is simply found again from the next scan.
  }
};

// Tells this phone's scans from another phone on the same account, so a copied QR shown at a second
// phone is answered "already used" rather than as this phone's own repeat.
export const deviceRef = () => {
  try {
    let ref = localStorage.getItem(DEVICE_STORAGE_KEY);
    if (!ref) {
      ref = `admin-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(DEVICE_STORAGE_KEY, ref);
    }
    return ref;
  } catch {
    return undefined;
  }
};

// iPadOS reports a desktop user agent, so the pointer is asked as well.
export const isCoarsePointer = () => window.matchMedia?.("(pointer: coarse)").matches || isTouch();

export const looksScanned = (code: string) => /\/ticket\//i.test(code) || /^[0-9a-f]{64}$/i.test(code);

export const statusOf = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status;

export const failureMessage = (error: unknown) => {
  const status = statusOf(error);
  if (status === 403) return "Kjo llogari nuk mund të skanojë bileta";
  if (status === 401) return "Sesioni ka skaduar";
  if (!status) return "Nuk ka lidhje · Bileta nuk u kontrollua";
  return "Bileta nuk u kontrollua";
};

// One context, unlocked by the driver's first tap: iOS keeps a context created from a camera
// callback silent.
let audioContext: AudioContext | null = null;

export const unlockAudio = () => {
  try {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    audioContext ??= new AudioContextClass();
    if (audioContext.state === "suspended") void audioContext.resume();
  } catch {
    audioContext = null;
  }
};

export const feedback = (ok: boolean) => {
  try {
    navigator.vibrate?.(ok ? 80 : [120, 80, 120]);
  } catch {
    // Not every browser vibrates.
  }
  try {
    if (!audioContext) return;
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    oscillator.frequency.value = ok ? 880 : 220;
    gain.gain.value = 0.15;
    oscillator.connect(gain);
    gain.connect(audioContext.destination);
    oscillator.start();
    oscillator.stop(audioContext.currentTime + (ok ? 0.15 : 0.45));
  } catch {
    // Sound is a nicety; the card is the answer.
  }
};
